import { gql, type TypedDocumentNode } from "@apollo/client";
import type {
  PackagesQuery,
  PackagesQueryVariables,
  PackageDetailQuery,
  PackageDetailQueryVariables,
  PackagePackingListQuery,
  PackagePackingListQueryVariables,
  PackageOrdersQuery,
  PackageOrdersQueryVariables,
  PackageCheckoutStatusQuery,
  PackageCheckoutStatusQueryVariables,
  PackageAttiresQuery,
  PackageAttiresQueryVariables,
  PackagePurchaserQuery,
  PackagePurchaserQueryVariables,
} from "@src/@types/api.d";
import { PackageDetails } from "../fragments/packages.fragments";

export const GetPackages: TypedDocumentNode<
  PackagesQuery,
  PackagesQueryVariables
> = gql`
  query Packages($eventId: UUID!, $groupId: UUID!) {
    getAseobiPackages(event_id: $eventId, aseobi_id: $groupId) {
      ...PackageDetails
    }
  }
  ${PackageDetails}
`;

export const GetPackageDetail: TypedDocumentNode<
  PackageDetailQuery,
  PackageDetailQueryVariables
> = gql`
  query PackageDetail($eventId: UUID!, $packageId: UUID!) {
    getAseobiPackage(event_id: $eventId, package_id: $packageId) {
      ...PackageDetails
    }
  }
  ${PackageDetails}
`;

export const GetPackagePackingList: TypedDocumentNode<
  PackagePackingListQuery,
  PackagePackingListQueryVariables
> = gql`
  query PackagePackingList($eventId: UUID!, $groupId: UUID!) {
    getAseobiPackingList(event_id: $eventId, aseobi_id: $groupId) {
      package_id
      component_id
      name
      unit
      quantity
      package_count
    }
  }
`;

export const GetPackageOrders: TypedDocumentNode<
  PackageOrdersQuery,
  PackageOrdersQueryVariables
> = gql`
  query PackageOrders($eventId: UUID!, $groupId: UUID) {
    getAseobiOrdersV2(event_id: $eventId, aseobi_id: $groupId) {
      reference
      payment_reference
      checkout_id
      aseobi_id
      currency
      payment_status
      payment_provider
      payment_total
      subtotal
      service_fee
      delivery_fee
      late_fee
      vat
      transaction_charge
      paid_amount
      paid_currency
      created_at
      contact_email
      contact_phone_number
      delivery_address
      delivery_city
      delivery_zip_code
      lines {
        id
        line_type
        quantity
        unit_price
        subtotal
        service_fee
        line_total
        currency
        vendor
        fulfilment_status
        attire {
          attire_id
          yards
          size_unit
          price_per_yard
          fee_percentage
        }
        package {
          package_id
          package_version
          name
          description
          cover_image
          images
          unit_price
          unit_fee
          subtotal
          fee_amount
          total
          components {
            id
            name
            description
            quantity
            unit
            images
          }
          component_totals {
            component_id
            name
            quantity
            unit
          }
        }
      }
    }
  }
`;

export const GetPackageCheckoutStatus: TypedDocumentNode<
  PackageCheckoutStatusQuery,
  PackageCheckoutStatusQueryVariables
> = gql`
  query PackageCheckoutStatus($eventId: UUID!, $reference: String!) {
    getAsoebiCheckoutStatus(event_id: $eventId, payment_reference: $reference) {
      checkout_id
      payment_reference
      status
      expires_at
      currency
      total_amount
    }
  }
`;

export const GetPackageAttires: TypedDocumentNode<
  PackageAttiresQuery,
  PackageAttiresQueryVariables
> = gql`
  query PackageAttires($eventId: UUID!, $groupId: UUID!) {
    getAseobiAttires(event_id: $eventId, aseobi_id: $groupId) {
      id
      aseobi_id
      name
      active
      vendor
      images
      currency_iso
      price_per_yard
      total_price_per_yard
      yards_available
      minimum_yards
      size_unit
    }
  }
`;

export const GetPackagePurchaser: TypedDocumentNode<
  PackagePurchaserQuery,
  PackagePurchaserQueryVariables
> = gql`
  query PackagePurchaser {
    user {
      uuid
      email
    }
  }
`;
